import * as React from 'react';
import { useLocation } from 'react-router-dom';
import {
  IconArticle,
  IconCoins,
  IconCreditCard,
  IconDashboard,
  IconDeviceDesktop,
  IconFileText,
  IconFlask,
  IconGift,
  IconHelp,
  IconKey,
  IconPencil,
  IconSettings,
  IconServerCog,
  IconSparkles,
  IconTicket,
  IconUsers,
  IconHome,
  IconCurrencyDollar,
  IconWorld,
  IconActivity,
  IconPhoto,
  IconMessages,
} from '@tabler/icons-react';

import { NavMainItem } from '@/admin/components/nav-main';
import { NavSecondary } from '@/admin/components/nav-secondary';
import { NavUser } from '@/admin/components/nav-user';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/admin/components/ui/sidebar';
import { useAuth } from '@/admin/contexts/auth-context';
import { AppLogo } from '@/assets/logos/app-logo';
import { cn } from '@/admin/lib/utils';

export const adminNavData = {
  zones: [
    {
      id: 'home',
      title: 'Home',
      icon: IconHome,
      items: [
        { title: 'Dashboard', url: '/', icon: IconDashboard },
        { title: 'Users', url: '/users', icon: IconUsers },
        { title: 'Devices', url: '/devices', icon: IconDeviceDesktop },
        { title: 'Token Usage', url: '/usage', icon: IconCoins },
      ],
    },
    {
      id: 'platform',
      title: 'Platform',
      icon: IconServerCog,
      items: [
        { title: 'Providers', url: '/platform/providers', icon: IconServerCog },
        { title: 'Models', url: '/platform/models', icon: IconSettings },
        { title: 'Routes', url: '/platform/routes', icon: IconActivity },
        {
          title: 'Service Bindings',
          url: '/platform/service-bindings',
          icon: IconKey,
        },
      ],
    },
    {
      id: 'willow',
      title: 'Willow',
      icon: IconSparkles,
      items: [
        { title: 'Groups', url: '/willow/groups', icon: IconUsers },
        { title: 'Operations', url: '/willow/operations', icon: IconActivity },
        { title: 'Conversations', url: '/conversations', icon: IconMessages },
        { title: 'Templates', url: '/willow/templates', icon: IconFileText },
        {
          title: 'Initial Agents',
          url: '/willow/initial-agents',
          icon: IconSparkles,
        },
      ],
    },
    {
      id: 'price',
      title: 'Price',
      icon: IconCurrencyDollar,
      items: [
        { title: 'Plans', url: '/plans', icon: IconCreditCard },
        { title: 'Add-ons', url: '/addons', icon: IconGift },
        { title: 'Redeem Codes', url: '/redeem-codes', icon: IconTicket },
        { title: 'Invite Codes', url: '/invite-codes', icon: IconKey },
      ],
    },
    {
      id: 'seo',
      title: 'SEO',
      icon: IconWorld,
      items: [
        { title: 'Skills', url: '/skills', icon: IconSparkles },
        { title: 'Skill Comments', url: '/skill-comments', icon: IconPencil },
        {
          title: 'Blogs',
          url: '/blogs',
          icon: IconArticle,
          isBlogList: true,
        },
        { title: 'Book / Gallery', url: '/gallery', icon: IconPhoto },
      ],
    },
    {
      id: 'evaluator',
      title: 'Evaluator',
      icon: IconActivity,
      items: [
        { title: 'Benchmark', url: '/benchmark', icon: IconFlask },
        { title: 'Evals', url: '/evals', icon: IconActivity },
        { title: 'Audit Logs', url: '/audit', icon: IconFileText },
      ],
    },
  ],
  navSecondary: [
    {
      title: 'Settings',
      url: '/settings',
      icon: IconSettings,
    },
    {
      title: 'Help',
      url: '/help',
      icon: IconHelp,
    },
  ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const location = useLocation();
  const isZoneActive = React.useCallback(
    (zone: (typeof adminNavData.zones)[number]) =>
      zone.items.some(
        item =>
          location.pathname === item.url ||
          location.pathname.startsWith(`${item.url}/`)
      ),
    [location.pathname]
  );

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:p-1.5!"
            >
              <a href="/" className="flex items-center gap-2">
                <AppLogo className="size-5!" />
                <span className="text-base font-semibold">Admin Panel</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {adminNavData.zones.map(zone => {
          const Icon = zone.icon;
          const active = isZoneActive(zone);
          return (
            <SidebarGroup key={zone.id}>
              <SidebarGroupLabel
                className={cn(
                  'gap-2 text-xs font-semibold uppercase tracking-wide',
                  active && 'text-sidebar-foreground'
                )}
              >
                <Icon className="size-4" />
                <span>{zone.title}</span>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {zone.items.map(item => (
                    <NavMainItem key={item.title} item={item} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
        <NavSecondary items={adminNavData.navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>{user && <NavUser user={user} />}</SidebarFooter>
    </Sidebar>
  );
}
